import DataTable from 'react-data-table-component'
import PageLayout from '../../common/components/PageLayout/PageLayout'
import TextSeparator from '../../common/components/TextSeparator/TextSeparator'
import Title from '../../common/components/Title/Title'
import './Contribution.css'
import { columns, data } from './datatable'
import TextRef from '../../common/components/TextRef/TextRef'
import PageLink from '../../common/components/PageLink/PageLink'
import References from '../../common/components/References/References'
import MarginDecoration from '../../common/components/MarginDecoration/MarginDecoration'

function Contribution () {
  return (
    <PageLayout title="Contribution">
      <MarginDecoration />
      <p>
        Amongst our work, we first wanted to contribute to future iGEM in teams
        in two forms; add new parts to the registry and bring new details to
        past parts used as controls in our experiments.
      </p>
      <TextSeparator />
      <Title isMid title="New parts" />
      <p>
        Part{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K4726000">
          BBa K4726000
        </PageLink>{' '}
        (that we named P0DTR4 throughout this work) encodes a
        N-acetyl-&alpha;-D-galactosamine deacetylase from{' '}
        <i>Flavonifractor plautii</i> originating from the human gut{' '}
        <TextRef sup="1" content="microbiota" />. This enzyme is active towards
        A antigen and enzymatically removes the acetyl moiety of the
        aforementioned antigen. The product however does not correspond to O
        antigen as a &alpha;-D-galactosamine residue remains. As of the end of
        this iGEM cycle, we were not able to express P0DTR4, however all other
        expressed enzymes had shown significant proteotoxicity. We can consider
        that this enzyme would probably show a similar proteotoxicity. We thus
        recommended to use <i>Escherichia coli</i> Rosetta (DE3) pLysS which
        relieves the burden of agglomerating misfolded proteins.
      </p>
      <p>
        Part{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K4726001">
          BBa K4726001
        </PageLink>{' '}
        (that we named P0DTR5 throughout this work) encodes a
        &alpha;-D-galactosamine galactosaminidase also from{' '}
        <i>Flavonifractor plautii</i> <TextRef sup="1" content="" />. This
        enzyme works in pair with P0DTR4 to fully remove the A antigen,
        producing the H antigen, most commonly known as O blood. In that regard,
        P0DTR5 is not active towards A antigen without prior reaction with
        P0DTR4. Due to the same proteotoxicity, using <i>E. coli</i> Rosetta
        (DE3) pLysS allowed to express the enzyme in visible high yield. Using
        both would prove to be more efficient than previous
        &alpha;-N-Acetylgalactosaminidase (NAGA) from{' '}
        <i>Elizabethkingia meningoseptica</i> (
        <PageLink src="http://parts.igem.org/Part:BBa_K3717016">
          BBa K3717016
        </PageLink>
        ) as both P0DTR4 and P0DTR5 were shown to efficiently convert A antigen
        to O without the need of cofactors and supporting matrix.
      </p>
      <p>
        Part{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K4726002">
          BBa K4726002
        </PageLink>{' '}
        (that we named GH110b throughout this work) encodes a
        &alpha;-galactosidase originating from <i>Bacteroides fragilis</i>
        <TextRef sup="2" content="" />. This enzyme is active towards B antigen
        and thus removes the &alpha;-galactosyl moiety leading to H antigen.
        This enzyme has a similar affinity and catalytic activity towards B
        antigen as the previously used &alpha;-galactosidase (GH110a) from the
        same bacteria (
        <PageLink src="http://parts.igem.org/Part:BBa_K3717006">
          BBa K3717006
        </PageLink>
        ). However, GH110b was shown to be active towards both branched and
        linear B antigen, whereas GH110a is only towards branched B antigen.
        This could prove to be promising for xenotransplantation purposes.
        Again, for the same proteotoxicity, using <i>E. coli</i> Rosetta (DE3)
        pLysS allowed to express the enzyme in visible high yield.
      </p>
      <TextSeparator />
      <Title isMid title="Contribution to previous parts" />
      <p>
        Although we wanted to bring further detailed information to previous
        parts{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K3717016">
          BBa K3717016
        </PageLink>{' '}
        (NAGA) and{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K3717006">
          BBa K3717006
        </PageLink>{' '}
        (GH110a), we were not able to go beyond qualitative enzymatic assays.
        Most particularly, only{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K3717006">
          BBa K3717006
        </PageLink>{' '}
        was expressed and used amongst the two.
      </p>
      <br />
      <DataTable columns={columns} data={data} responsive />
      <br />
      <p>
        We still noted during expression that a large amount of GH110a was in
        the insoluble extract. Considering iGEM Taipei 2021 also had low yield
        of the aforementioned protein, we first recommend to use <i>E. coli</i>{' '}
        Rosetta (DE3) pLysS as the host for protein expression, since it
        resolved the apparent proteotoxicity we had. Further expression
        optimization would be required to increase the proportion of protein in
        the soluble extract.
      </p>
      <p>
        Here we noted some errors in a specific parts (
        <PageLink src="http://parts.igem.org/Part:BBa_K3717006">
          BBa K3717006
        </PageLink>
        ) that slipped in the registry and in the wiki of iGEM Taipei 2021.
        First, we noted in their enzyme selection and description that they
        misidentified the parts as GH110b from <i>B. fragilis</i>. We verified
        in the source <TextRef sup="2" content="article" />, as well as the
        GenBank entries and compared sequences, and finally noted that their
        part is indeed the GH110a and not GH110b. In that regard, iGEM Taipei
        used GH110a in their work, and we used GH110b in ours. Furthermore, in
        their registry references, the wrong UniProt entry was referenced and
        linked. We thus added our correction in the part{' '}
        <PageLink src="http://parts.igem.org/Part:BBa_K3717006">
          BBa K3717006
        </PageLink>{' '}
        entry.
      </p>
      <References
        references={[
          <span key={1}>
            Rahfeld P, Sim L, Moon H, Constantinescu I, Morgan-Lang C, Hallam
            SJ, Kizhakkedathu JN, Withers SG. An enzymatic pathway in the human
            gut microbiome that converts A to universal O type blood. Nat
            Microbiol. 2019 Sep;4(9):1475-1485. doi: 10.1038/s41564-019-0469-7.
            Epub 2019 Jun 10. PMID: 31182795.
          </span>,
          <span key={2}>
            Liu QP, Yuan H, Bennett EP, Levery SB, Nudelman E, Spence J, Pietz
            G, Saunders K, White T, Olsson ML, Henrissat B, Sulzenbacher G,
            Clausen H. Identification of a GH110 subfamily of alpha
            1,3-galactosidases: novel enzymes for removal of the alpha 3Gal
            xenotransplantation antigen. J Biol Chem. 2008 Mar
            28;283(13):8545-54. doi: 10.1074/jbc.M709020200. Epub 2008 Jan 28.
            PMID: 18227066; PMCID: PMC2417185.
          </span>
        ]}
      />
    </PageLayout>
  )
}

export default Contribution
